/**
 * LO QUE LOS MÉDICOS RECETARON Y NO TENÍAMOS.
 *
 * La lista de la compra de verdad, y la única que no se puede sacar del
 * inventario: el inventario dice qué se vendió, no qué se pidió y se fue a
 * comprar enfrente. Ese paciente no aparece en ningún informe de ventas porque
 * nunca llegó a ser una venta.
 *
 * Vive en Compras y no en una pantalla propia porque quien entra aquí es quien
 * va a comprar. Una pantalla más en el menú es una pantalla más que nadie abre.
 *
 * El dato se venía guardando desde hace tiempo —cada ítem de receta anota el
 * stock que había al recetarlo— y no lo miraba nadie.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ShoppingBasket, TriangleAlert } from "lucide-react";
import { demandaNoCubierta } from "../../api/recetas";
import { useZonaHoraria } from "../../auth/TenantBrandingProvider";
import { fechaDeInstante } from "../../lib/fecha";
import { Badge, Card, CardBody, CardHeader } from "../ui";

const VENTANAS = [
  { dias: 30, etiqueta: "30 días" },
  { dias: 90, etiqueta: "3 meses" },
  { dias: 365, etiqueta: "1 año" },
];

export function DemandaNoCubiertaCard() {
  const tz = useZonaHoraria();
  const [dias, setDias] = useState(90);

  const { data: filas = [], isLoading } = useQuery({
    queryKey: ["recetas", "demanda-no-cubierta", dias],
    queryFn: () => demandaNoCubierta(dias),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 font-semibold text-slate-900">
              <ShoppingBasket
                className="h-4 w-4 text-[color:var(--color-link)]"
                aria-hidden="true"
              />
              Lo que se recetó y no teníamos
              {filas.length > 0 && <Badge variant="warning">{filas.length}</Badge>}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Pacientes que salieron a comprar fuera. Es la demanda que se está
              perdiendo, y no sale en ningún informe de ventas.
            </p>
          </div>
          <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
            {VENTANAS.map((v) => (
              <button
                key={v.dias}
                type="button"
                onClick={() => setDias(v.dias)}
                className={`rounded-md px-3 py-1 text-xs font-medium transition ${
                  dias === v.dias
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {v.etiqueta}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        {isLoading && (
          <p className="px-6 py-8 text-center text-sm text-slate-400">Cargando…</p>
        )}
        {!isLoading && filas.length === 0 && (
          <p className="px-6 py-10 text-center text-sm text-slate-500">
            Nada. Todo lo que se recetó en este periodo estaba en el anaquel.
          </p>
        )}
        {filas.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-6 py-2 font-medium">Medicamento</th>
                  <th className="px-3 py-2 text-right font-medium">Recetas</th>
                  <th className="px-3 py-2 text-right font-medium">Unidades</th>
                  <th className="px-6 py-2 text-right font-medium">Última vez</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filas.map((f) => (
                  <tr key={`${f.producto_id ?? "libre"}-${f.descripcion}`}>
                    <td className="px-6 py-3">
                      <span className="font-medium text-slate-900">
                        {f.descripcion}
                      </span>
                      {/* Sin catálogo no es reponer, es empezar a tenerlo: la
                          decisión es otra y el precio hay que buscarlo. */}
                      {!f.en_catalogo && (
                        <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-700">
                          <TriangleAlert className="h-3 w-3" />
                          no está en el catálogo
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right font-mono text-slate-700">
                      {f.veces}
                    </td>
                    <td className="px-3 py-3 text-right font-mono text-slate-700">
                      {f.cantidad_pedida}
                    </td>
                    <td className="px-6 py-3 text-right text-slate-500">
                      {f.ultima_vez ? fechaDeInstante(f.ultima_vez, tz) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
